import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getScriptDisplayTitle,
  isUsableScriptText,
} from "@/features/opportunities/lib/script-display-title";
import {
  shareLinkQueryOptions,
  type SharePayload,
  type ShareStoryboardImage,
} from "@/lib/queries";

export const Route = createFileRoute("/s/$token")({
  component: SharePage,
});

// Editorial serif stack — distinctive on Apple platforms, graceful Georgia fallback elsewhere.
const SERIF =
  "font-['Iowan_Old_Style','Palatino_Linotype','Palatino','Hoefler_Text','Georgia',serif]";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// navigator.clipboard is undefined in non-secure contexts and writeText can
// reject, so guard and treat any failure as a no-op.
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function buildCopyText(data: SharePayload, untitled: string): string {
  const lines: string[] = [];
  const displayTitle = getScriptDisplayTitle(
    { hook: data.hook ?? null, concept: data.concept ?? null },
    untitled,
  );
  lines.push(displayTitle);
  lines.push("");
  if (data.adDescription) { lines.push(data.adDescription); lines.push(""); }
  if (data.sequence) lines.push(`Sequence: ${data.sequence}`);
  // Only list Concept as metadata when hook already provided the title line.
  if (isUsableScriptText(data.hook) && isUsableScriptText(data.concept)) {
    lines.push(`Concept: ${data.concept!.trim()}`);
  }
  if (data.duration) lines.push(`Duration: ${data.duration}`);
  if (data.presenter) lines.push(`Presenter: ${data.presenter}`);
  if (data.background) lines.push(`Background: ${data.background}`);
  if (data.product) lines.push(`Product: ${data.product}`);
  if (data.opportunityTitle) lines.push(`From: ${data.opportunityTitle}`);
  const hasSpecs =
    data.sequence ||
    (isUsableScriptText(data.hook) && isUsableScriptText(data.concept)) ||
    data.duration ||
    data.presenter ||
    data.background ||
    data.product ||
    data.opportunityTitle;
  if (hasSpecs) lines.push("");
  for (const sc of data.scenes) {
    lines.push(`Scene ${sc.scene_number} — ${sc.scene_name}`);
    lines.push(`  Seeing: ${sc.seeing}`);
    lines.push(`  Hearing: ${sc.hearing}`);
    if (sc.product) lines.push(`  Product: ${sc.product}`);
    if (sc.props) lines.push(`  Props: ${sc.props}`);
  }
  if (data.scenes.length > 0) lines.push("");
  if (data.script) lines.push(data.script);
  if (data.rationale) { lines.push(""); lines.push("Why this works"); lines.push(data.rationale); }
  return lines.join("\n").trim();
}

// ─── Mobile scene field ─────────────────────────────────────────────────────
// A 5-column table is unreadable on phones, so on small screens each scene is
// rendered as a stacked block of labelled fields instead.

function SceneField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">
        {label}
      </dt>
      <dd className="text-[15px] leading-relaxed text-stone-700">{value}</dd>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function SharePage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const query = useQuery(shareLinkQueryOptions(token));
  const data = query.data;
  const untitled = t("sharePage.untitledTitle", "Untitled script");
  const displayTitle = data
    ? getScriptDisplayTitle(
        { hook: data.hook ?? null, concept: data.concept ?? null },
        untitled,
      )
    : null;

  const [scriptOpen, setScriptOpen] = React.useState(false);
  const [scriptCopied, setScriptCopied] = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);

  // Referrer-Policy: no-referrer — token is a capability URL, don't leak it
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  // Dynamic document title
  React.useEffect(() => {
    document.title = displayTitle
      ? `${displayTitle} · Adge AI`
      : "Shared Script · Adge AI";
    return () => { document.title = "Adge AI"; };
  }, [displayTitle]);

  function copyScript() {
    if (!data) return;
    void copyToClipboard(buildCopyText(data, untitled)).then((ok) => {
      if (!ok) return;
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    });
  }

  function copyLink() {
    void copyToClipboard(window.location.href).then((ok) => {
      if (!ok) return;
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  // Signing can fail per-image, in which case the API sends imageUrl: null —
  // show nothing rather than a broken frame on a page sent to an outsider.
  const storyboardImages = (data?.storyboardImages ?? []).filter(
    (img): img is ShareStoryboardImage & { imageUrl: string } => !!img.imageUrl,
  );

  const metaRows = data
    ? [
        { label: "Concept", value: data.concept },
        { label: "Product", value: data.product },
        { label: "Duration", value: data.duration },
        { label: "Presenter", value: data.presenter },
        { label: "Background", value: data.background },
      ].filter((row): row is { label: string; value: string } =>
        isUsableScriptText(row.value),
      )
    : [];

  return (
    <div className="min-h-dvh flex flex-col bg-[#faf9f7] text-stone-900 antialiased selection:bg-amber-200/60">
      <header className="sticky top-0 z-10 border-b border-stone-200/70 bg-[#faf9f7]/80 backdrop-blur-sm">
        <div className="mx-auto flex h-14 w-full max-w-3xl items-center justify-between gap-4 px-5">
          <div className="flex min-w-0 items-center gap-2.5">
            <a
              href="https://adge.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 whitespace-nowrap rounded text-sm font-semibold tracking-tight text-stone-900 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40"
            >
              Adge AI
            </a>
            <span className="hidden h-3.5 w-px bg-stone-300 sm:inline-block" />
            <span className="hidden whitespace-nowrap text-[13px] text-stone-400 sm:inline">Shared script</span>
          </div>
          {data && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={copyLink}
                className="inline-flex min-h-11 items-center whitespace-nowrap rounded-md border border-stone-300 px-3 text-[13px] text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40"
              >
                {linkCopied ? "Copied!" : "Copy link"}
              </button>
              <button
                onClick={copyScript}
                className="inline-flex min-h-11 items-center whitespace-nowrap rounded-md bg-stone-900 px-3 text-[13px] font-medium text-stone-50 transition-colors hover:bg-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#faf9f7]"
              >
                {scriptCopied ? "Copied!" : "Copy script"}
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14 sm:py-20">
        {query.isLoading && (
          <div className="space-y-6" aria-busy="true">
            <div className="h-3 w-40 animate-pulse rounded bg-stone-200" />
            <div className="space-y-3">
              <div className="h-7 w-full animate-pulse rounded bg-stone-200" />
              <div className="h-7 w-2/3 animate-pulse rounded bg-stone-200" />
            </div>
            <div className="h-20 w-full animate-pulse rounded bg-stone-100" />
          </div>
        )}

        {query.isError && (
          <div className="flex flex-col items-center justify-center gap-4 py-28 text-center">
            <p className={`${SERIF} text-2xl text-stone-800`}>This link has expired.</p>
            <p className="max-w-sm text-sm text-stone-500">
              Shared scripts are available for a limited time. Ask the sender for a fresh link.
            </p>
            <a
              href="https://adge.ai"
              className="mt-1 text-sm font-medium text-amber-700 underline-offset-4 hover:underline"
            >
              Go to Adge AI →
            </a>
          </div>
        )}

        {data && (
          <article className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-700">
            <section className="motion-safe:animate-in motion-safe:slide-in-from-bottom-3 motion-safe:fill-mode-both motion-safe:duration-700">
              {(data.opportunityTitle || data.updatedAt) && (
                <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                  {data.opportunityTitle}
                  {data.opportunityTitle && data.updatedAt && (
                    <span className="text-amber-700/40"> · </span>
                  )}
                  {data.updatedAt && (
                    <span className="text-stone-400">{fmtDate(data.updatedAt)}</span>
                  )}
                </p>
              )}

              {displayTitle && (
                <h1 className={`${SERIF} text-balance text-[1.9rem] font-semibold leading-[1.15] tracking-[-0.01em] text-stone-900 sm:text-[2.4rem]`}>
                  {displayTitle}
                </h1>
              )}

              {data.adDescription && (
                <p className="mt-6 max-w-prose text-[1.0625rem] leading-relaxed text-stone-600">
                  {data.adDescription}
                </p>
              )}
            </section>

            {storyboardImages.length > 0 && (
              <section className="mt-10 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-75 motion-safe:duration-700">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
                  {t("sharePage.storyboard.title", "Storyboard")}
                </p>
                <div className="space-y-4">
                  {storyboardImages.map((img, index) => (
                    <img
                      key={`${img.model}-${index}`}
                      src={img.imageUrl}
                      alt={t("sharePage.storyboard.imageAlt", "Storyboard for this script")}
                      loading="lazy"
                      className="aspect-video w-full rounded-md border border-stone-200 bg-stone-100 object-cover"
                    />
                  ))}
                </div>
              </section>
            )}

            {metaRows.length > 0 && (
              <dl className="mt-10 divide-y divide-stone-200 border-y border-stone-200 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-100 motion-safe:duration-700">
                {metaRows.map((row) => (
                  <div key={row.label} className="grid grid-cols-[6.5rem_1fr] gap-5 py-3 sm:grid-cols-[8rem_1fr]">
                    <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
                      {row.label}
                    </dt>
                    <dd className="text-[15px] leading-relaxed text-stone-700">{row.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {data.scenes.length > 0 && (
              <section className="mt-14 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-150 motion-safe:duration-700">
                <div className="flex items-baseline justify-between border-b border-stone-300 pb-2.5">
                  <h2 className={`${SERIF} text-xl font-semibold text-stone-900`}>Scene breakdown</h2>
                  <span className="text-xs tabular-nums text-stone-400">
                    {data.scenes.length} {data.scenes.length === 1 ? "scene" : "scenes"}
                  </span>
                </div>
                {/* Mobile (<sm): stacked, full-width scene blocks */}
                <ul className="divide-y divide-stone-200 sm:hidden">
                  {data.scenes.map((sc) => (
                    <li key={`m-${sc.scene_number}-${sc.scene_name}`} className="py-5">
                      <div className="mb-3 flex items-baseline gap-2.5">
                        <span className="font-mono text-xs tabular-nums text-stone-400">
                          {String(sc.scene_number).padStart(2, "0")}
                        </span>
                        <h3 className="font-medium text-stone-800">{sc.scene_name}</h3>
                      </div>
                      <dl className="space-y-3">
                        <SceneField label="Seeing" value={sc.seeing} />
                        <SceneField label="Hearing" value={sc.hearing} />
                        {sc.props && <SceneField label="Props" value={sc.props} />}
                        {sc.product && <SceneField label="Product" value={sc.product} />}
                      </dl>
                    </li>
                  ))}
                </ul>

                {/* Desktop (sm+): full scene table */}
                <div className="hidden overflow-x-auto sm:block">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="text-left align-bottom">
                        {["", "Scene", "Seeing", "Hearing", "Props"].map((h, i) => (
                          <th
                            key={h || "num"}
                            className={`py-2.5 pr-4 text-[10px] font-semibold uppercase tracking-wider text-stone-400 ${i === 0 ? "w-8" : ""}`}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {data.scenes.map((sc) => (
                        <tr
                          key={`${sc.scene_number}-${sc.scene_name}`}
                          className="align-top transition-colors hover:bg-stone-100/60"
                        >
                          <td className="py-4 pr-4 font-mono text-xs text-stone-400 tabular-nums">
                            {String(sc.scene_number).padStart(2, "0")}
                          </td>
                          <td className="py-4 pr-4 font-medium text-stone-800">{sc.scene_name}</td>
                          <td className="py-4 pr-4 leading-relaxed text-stone-600">{sc.seeing}</td>
                          <td className="py-4 pr-4 leading-relaxed text-stone-600">{sc.hearing}</td>
                          <td className="py-4 leading-relaxed text-stone-400">{sc.props || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {data.script && (
              <section className="mt-12 border-t border-stone-200 pt-6">
                <button
                  onClick={() => setScriptOpen((o) => !o)}
                  className="group flex w-full items-center justify-between gap-4 text-left focus-visible:outline-none"
                  aria-expanded={scriptOpen}
                >
                  <span className={`${SERIF} text-xl font-semibold text-stone-900 group-focus-visible:underline group-focus-visible:underline-offset-4`}>
                    Full script
                  </span>
                  <svg
                    className={`h-4 w-4 shrink-0 text-stone-400 transition-transform duration-300 group-hover:text-stone-600 ${scriptOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${scriptOpen ? "mt-4 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-stone-700">
                      {data.script}
                    </pre>
                  </div>
                </div>
              </section>
            )}

            {data.rationale && (
              <aside className="mt-14 border-l-2 border-amber-600/70 pl-6">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                  Why this works
                </p>
                <p className={`${SERIF} text-lg leading-relaxed text-stone-700`}>
                  {data.rationale}
                </p>
              </aside>
            )}
          </article>
        )}
      </main>

      <footer className="border-t border-stone-200/70 px-5 py-7 text-center text-xs text-stone-400">
        Shared via{" "}
        <a href="https://adge.ai" className="text-stone-500 underline-offset-2 hover:underline">
          Adge AI
        </a>
        {data?.updatedAt && ` · last updated ${fmtDate(data.updatedAt)}`}. Content reflects the latest
        version of the script.
      </footer>
    </div>
  );
}
